import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * Lightbox carousel for design-dependency images. Driven by an array of
 * `images` ({id, name, url, content_type}) and a starting index. Calls
 * `/api/design-dependencies/{depId}/images/{imageId}` when no public URL is
 * present so private storage images render correctly under auth.
 */
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ImageLightbox = ({ depId, images, openIndex, onClose }) => {
  const [idx, setIdx] = useState(openIndex || 0);
  useEffect(() => { setIdx(openIndex || 0); }, [openIndex]);

  if (openIndex == null || !images || images.length === 0) return null;
  const img = images[idx];
  if (!img) return null;
  const token = localStorage.getItem('siddhi_token') || '';
  const src = img.url || `${API}/design-dependencies/${depId}/images/${img.id}?token=${encodeURIComponent(token)}`;

  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);

  return (
    <Dialog open={openIndex != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-0" data-testid="dep-image-lightbox">
        <div className="relative">
          <img
            src={src}
            alt={img.name || `Image ${idx + 1}`}
            className="w-full max-h-[80vh] object-contain bg-black"
            data-testid="dep-image-lightbox-img"
          />
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-2 right-2 text-white hover:bg-white/10 h-9 w-9"
            onClick={onClose}
            data-testid="dep-image-lightbox-close"
          >
            <X className="h-5 w-5" />
          </Button>
          {images.length > 1 && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-10 w-10"
                onClick={prev}
                data-testid="dep-image-lightbox-prev"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-10 w-10"
                onClick={next}
                data-testid="dep-image-lightbox-next"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
          <div className="px-4 py-2 text-xs text-white/80 bg-black/70 text-center">
            {idx + 1} / {images.length} · {img.name}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageLightbox;
export { API };
